import { paceScript, totalReadingSeconds } from "./prompter";

/**
 * The pace a meditation is read at, in words per minute.
 *
 * A guided meditation is read far slower than conversational speech (~150
 * wpm): the silences are the work. The default sits at the slow end, and the
 * bounds stop the prompter from either racing the therapist or standing still.
 *
 * Values MUST stay in sync with `domain.models.meditation` on the API.
 */
export const MIN_READING_SPEED_WPM = 40;
export const MAX_READING_SPEED_WPM = 160;
export const DEFAULT_READING_SPEED_WPM = 90;

/** Named marks on the pace slider, so the number means something. */
export const READING_SPEED_MARKS = [
    { wpm: 60, labelKey: "meditation.pace.verySlow" },
    { wpm: 90, labelKey: "meditation.pace.slow" },
    { wpm: 120, labelKey: "meditation.pace.conversational" },
] as const;

/** The nearest named pace to a value, for the label under the slider. */
export function paceLabelKey(wpm: number): string {
    return READING_SPEED_MARKS.reduce((closest, mark) =>
        Math.abs(mark.wpm - wpm) < Math.abs(closest.wpm - wpm) ? mark : closest
    ).labelKey;
}

/**
 * How long a script runs, and how many words it is.
 *
 * Shown while writing so a therapist aiming at a ten-minute meditation finds
 * out before the session rather than during it. It is the prompter's own
 * arithmetic — pauses between paragraphs included — so the estimate and the
 * reading cannot disagree.
 */
export function estimateFromScript(
    script: string,
    wordsPerMinute: number
): { words: number; seconds: number } {
    const segments = paceScript(script, wordsPerMinute);
    return {
        // The silences a therapist wrote are not words, and counting them as
        // such would say a meditation is longer to write than it is.
        words: segments.reduce((total, segment) => total + segment.words, 0),
        seconds: Math.round(totalReadingSeconds(segments)),
    };
}
